/**
 * 导入数据相关的工具函数
 * 用于处理从后端返回的节点和边数据，生成坐标并避免与现有图形重叠
 */

import { GroupType } from '../types/nodeTypes';

interface ImportedNode {
  id: string;
  type: string;
  properties: {
    name: string;
    description?: string;
    parentGroupId?: string;
    children?: string[];
    [key: string]: any;
  };
  children?: string[];
}

interface ImportedEdge {
  id: string;
  type: string;
  sourceNodeId: string;
  targetNodeId: string;
  text?: string;
  properties?: Record<string, any>;
}

interface ImportedData {
  nodes: ImportedNode[];
  edges: ImportedEdge[];
}

interface ExistingNode {
  id: string;
  x: number;
  y: number;
  width: number;
  height: number;
  type: string;
  [key: string]: any;
}

interface PositionedNode {
  id: string;
  type: string;
  x: number;
  y: number;
  width: number;
  height: number;
  text: {
    value: string;
    x: number;
    y: number;
  };
  properties: Record<string, any>;
  children?: string[];
}

interface BoundingBox {
  minX: number;
  maxX: number;
  minY: number;
  maxY: number;
}

/**
 * 获取节点的默认尺寸
 */
const getDefaultNodeSize = (
  type: string,
  childCount: number = 0
): { width: number; height: number } => {
  switch (type) {
    case 'rect':
      return { width: 180, height: 80 };
    case 'polygon':
      return { width: 150, height: 80 };
    case 'circle':
      return { width: 100, height: 100 };
    case 'diamond':
      return { width: 120, height: 120 };
    case 'state':
      return { width: 70, height: 30 };
    case 'value':
      return { width: 70, height: 30 };
    case 'dynamic-group':
      // 根据子节点数量动态计算分组大小
      const stateWidth = 70;
      const stateHeight = 30;
      const padding = 30;
      const stateSpacing = 10;
      const statesPerRow = 2;

      const rows = Math.ceil(childCount / statesPerRow) || 1;
      const cols = Math.min(childCount, statesPerRow) || 1;

      const width = Math.max(
        180,
        padding * 2 + cols * stateWidth + (cols - 1) * stateSpacing + 40
      );
      const height = Math.max(
        100,
        padding * 2 + rows * stateHeight + (rows - 1) * stateSpacing + 40
      );

      return { width, height };
    default:
      return { width: 150, height: 80 };
  }
};

/**
 * 计算现有节点的边界框
 */
const calculateExistingBounds = (nodes: ExistingNode[]): BoundingBox => {
  if (nodes.length === 0) {
    return { minX: 0, maxX: 0, minY: 0, maxY: 0 };
  }

  let minX = Infinity;
  let maxX = -Infinity;
  let minY = Infinity;
  let maxY = -Infinity;

  nodes.forEach(node => {
    const halfWidth = (node.width || 100) / 2;
    const halfHeight = (node.height || 60) / 2;

    minX = Math.min(minX, node.x - halfWidth);
    maxX = Math.max(maxX, node.x + halfWidth);
    minY = Math.min(minY, node.y - halfHeight);
    maxY = Math.max(maxY, node.y + halfHeight);
  });

  return { minX, maxX, minY, maxY };
};

/**
 * 检查两个矩形是否重叠
 */
const isOverlapping = (
  x1: number,
  y1: number,
  w1: number,
  h1: number,
  x2: number,
  y2: number,
  w2: number,
  h2: number,
  padding: number = 20
): boolean => {
  const left1 = x1 - w1 / 2 - padding;
  const right1 = x1 + w1 / 2 + padding;
  const top1 = y1 - h1 / 2 - padding;
  const bottom1 = y1 + h1 / 2 + padding;

  const left2 = x2 - w2 / 2;
  const right2 = x2 + w2 / 2;
  const top2 = y2 - h2 / 2;
  const bottom2 = y2 + h2 / 2;

  return !(
    right1 < left2 ||
    left1 > right2 ||
    bottom1 < top2 ||
    top1 > bottom2
  );
};

/**
 * 检查位置是否与现有节点重叠
 */
const isPositionValid = (
  x: number,
  y: number,
  width: number,
  height: number,
  existingNodes: ExistingNode[],
  newlyPositionedNodes: PositionedNode[],
  padding: number = 30
): boolean => {
  // 检查与现有节点的重叠
  for (const node of existingNodes) {
    if (
      isOverlapping(
        x,
        y,
        width,
        height,
        node.x,
        node.y,
        node.width || 100,
        node.height || 60,
        padding
      )
    ) {
      return false;
    }
  }

  // 检查与已定位的新节点的重叠
  for (const node of newlyPositionedNodes) {
    if (
      isOverlapping(
        x,
        y,
        width,
        height,
        node.x,
        node.y,
        node.width,
        node.height,
        padding
      )
    ) {
      return false;
    }
  }

  return true;
};

/**
 * 构建节点的层级关系（用于按层布局）
 */
const buildNodeHierarchy = (
  nodes: ImportedNode[],
  edges: ImportedEdge[],
  rootNodeId: string
): Map<number, ImportedNode[]> => {
  const levelMap = new Map<number, ImportedNode[]>();
  const nodeIdSet = new Set(nodes.map(n => n.id));
  const visited = new Set<string>();

  // BFS 遍历构建层级
  const queue: { id: string; level: number }[] = [];

  // 找到与根节点直接相连的节点作为第一层
  edges.forEach(edge => {
    if (edge.sourceNodeId === rootNodeId && nodeIdSet.has(edge.targetNodeId)) {
      queue.push({ id: edge.targetNodeId, level: 1 });
    }
    if (edge.targetNodeId === rootNodeId && nodeIdSet.has(edge.sourceNodeId)) {
      queue.push({ id: edge.sourceNodeId, level: 1 });
    }
  });

  // 处理没有直接连接到根节点的节点
  const connectedNodes = new Set<string>();
  queue.forEach(item => connectedNodes.add(item.id));

  while (queue.length > 0) {
    const { id, level } = queue.shift()!;

    if (visited.has(id)) continue;
    visited.add(id);

    const node = nodes.find(n => n.id === id);
    if (!node) continue;

    // 添加到对应层级
    if (!levelMap.has(level)) {
      levelMap.set(level, []);
    }
    levelMap.get(level)!.push(node);

    // 找到相连的下一层节点
    edges.forEach(edge => {
      let nextNodeId: string | null = null;
      if (edge.sourceNodeId === id && nodeIdSet.has(edge.targetNodeId)) {
        nextNodeId = edge.targetNodeId;
      } else if (edge.targetNodeId === id && nodeIdSet.has(edge.sourceNodeId)) {
        nextNodeId = edge.sourceNodeId;
      }

      if (nextNodeId && !visited.has(nextNodeId)) {
        queue.push({ id: nextNodeId, level: level + 1 });
        connectedNodes.add(nextNodeId);
      }
    });
  }

  // 处理孤立节点（没有边连接的节点）
  const orphanNodes = nodes.filter(
    n => !connectedNodes.has(n.id) && n.id !== rootNodeId
  );
  if (orphanNodes.length > 0) {
    const maxLevel = Math.max(...Array.from(levelMap.keys()), 0);
    if (!levelMap.has(maxLevel + 1)) {
      levelMap.set(maxLevel + 1, []);
    }
    levelMap.get(maxLevel + 1)!.push(...orphanNodes);
  }

  return levelMap;
};

/**
 * 判断节点是否是子节点（属于某个分组的状态或值节点）
 */
const isChildNode = (node: ImportedNode): boolean => {
  return !!(
    node.properties?.parentGroupId ||
    node.type === 'state' ||
    node.type === 'value'
  );
};

/**
 * 完善分组节点的属性（确保与正常数据结构一致）
 * @param node 导入的节点
 * @param childIds 子节点ID列表
 * @param childNodes 子节点列表（用于判断分组类型）
 * @param groupHeight 分组高度
 */
const completeGroupNodeProperties = (
  node: ImportedNode,
  childIds: string[],
  childNodes: ImportedNode[] = [],
  groupHeight: number = 100
): Record<string, any> => {
  // 根据子节点类型判断分组类型
  // 如果子节点包含 value 类型，则为值分组；否则为状态分组
  const hasValueChildren = childNodes.some(child => child.type === 'value');
  const groupType = hasValueChildren ? GroupType.VALUE : GroupType.STATE;

  const baseProps = {
    nodeType: 'rect',
    width: 180,
    height: 100,
    isGroup: true,
    isEntityGroup: true,
    originalType: 'dynamic-group',
    groupType, // 设置分组类型，值分组会显示 [] 后缀
    isRestrict: true,
    autoResize: false,
    collapsible: false,
    isCollapsed: false,
    // 文字偏移到顶部
    textOffset: {
      x: 0,
      y: -groupHeight / 2 + 20, // 距离顶部 20px
    },
    textDraggable: true,
  };

  return {
    ...baseProps,
    ...node.properties,
    // 确保 children 使用我们计算的值，覆盖原有的
    children: childIds,
    name: node.properties?.name || '',
    description: node.properties?.description || '',
    // 确保 groupType 使用我们计算的值
    groupType,
    // 确保 textOffset 使用我们计算的值
    textOffset: {
      x: 0,
      y: -groupHeight / 2 + 20,
    },
  };
};

/**
 * 完善状态节点的属性
 */
const completeStateNodeProperties = (
  node: ImportedNode
): Record<string, any> => {
  const baseProps = {
    nodeType: 'state',
    isState: true,
    width: 70,
    height: 30,
  };

  return {
    ...baseProps,
    ...node.properties,
    name: node.properties?.name || '',
    description: node.properties?.description || '',
    parentGroupId: node.properties?.parentGroupId || '',
  };
};

/**
 * 完善值节点的属性
 */
const completeValueNodeProperties = (
  node: ImportedNode
): Record<string, any> => {
  const baseProps = {
    nodeType: 'value',
    isValue: true,
    width: 70,
    height: 30,
  };

  return {
    ...baseProps,
    ...node.properties,
    name: node.properties?.name || '',
    description: node.properties?.description || '',
    parentGroupId: node.properties?.parentGroupId || '',
  };
};

/**
 * 为导入的节点生成坐标
 * 核心算法：
 * 1. 先分离子节点和顶级节点
 * 2. 只对顶级节点进行层级布局
 * 3. 将子节点放置在其父分组内部
 */
export const generateNodePositions = (
  importedData: ImportedData,
  targetNodeId: string,
  existingNodes: ExistingNode[]
): { nodes: PositionedNode[]; edges: ImportedEdge[] } => {
  const { nodes: importedNodes, edges: importedEdges } = importedData;

  // 找到目标节点（锚点）
  const targetNode = existingNodes.find(n => n.id === targetNodeId);
  if (!targetNode) {
    console.error('找不到目标节点:', targetNodeId);
    return generateDefaultPositions(
      importedNodes,
      importedEdges,
      400,
      300,
      existingNodes
    );
  }

  // 分离子节点和顶级节点
  const childNodes = importedNodes.filter(n => isChildNode(n));
  const topLevelNodes = importedNodes.filter(n => !isChildNode(n));

  // 构建父子关系映射
  const parentChildMap = new Map<string, ImportedNode[]>();
  childNodes.forEach(child => {
    const parentId = child.properties?.parentGroupId;
    if (parentId) {
      if (!parentChildMap.has(parentId)) {
        parentChildMap.set(parentId, []);
      }
      parentChildMap.get(parentId)!.push(child);
    }
  });

  // 确定布局起始位置：目标节点右侧
  const startX = targetNode.x + (targetNode.width || 100) / 2 + 150;
  const startY = targetNode.y;

  // 只对顶级节点构建层级关系
  const levelMap = buildNodeHierarchy(
    topLevelNodes,
    importedEdges,
    targetNodeId
  );

  const positionedNodes: PositionedNode[] = [];
  const horizontalSpacing = 220;
  const verticalSpacing = 140;

  // 按层级布局顶级节点
  levelMap.forEach((nodesInLevel, level) => {
    const levelX = startX + (level - 1) * horizontalSpacing;
    const levelHeight = nodesInLevel.length * verticalSpacing;
    const levelStartY = startY - levelHeight / 2 + verticalSpacing / 2;

    nodesInLevel.forEach((node, index) => {
      // 获取该分组的子节点
      const children = parentChildMap.get(node.id) || [];
      const childIds = children.map(c => c.id);

      // 根据子节点数量计算分组大小
      const { width, height } = getDefaultNodeSize(node.type, children.length);

      let x = levelX;
      let y = levelStartY + index * verticalSpacing;

      // 检查位置是否有效，如果重叠则调整
      let attempts = 0;
      const maxAttempts = 20;
      while (
        !isPositionValid(x, y, width, height, existingNodes, positionedNodes) &&
        attempts < maxAttempts
      ) {
        y += verticalSpacing / 2;
        attempts++;
        if (attempts > 10) {
          x += horizontalSpacing / 2;
          y = levelStartY + index * verticalSpacing;
        }
      }

      // 构建完整的分组节点
      let properties: Record<string, any>;
      let textY = y; // 默认文字在中心

      if (node.type === 'dynamic-group') {
        properties = completeGroupNodeProperties(
          node,
          childIds,
          children,
          height
        );
        properties.width = width;
        properties.height = height;
        // 分组节点的文字定位到顶部
        textY = y - height / 2 + 20;
      } else {
        properties = {
          ...node.properties,
          width,
          height,
        };
      }

      const positionedNode: PositionedNode = {
        id: node.id,
        type: node.type,
        x,
        y,
        width,
        height,
        text: {
          value: node.properties?.name || '',
          x,
          y: textY,
        },
        properties,
        children: node.type === 'dynamic-group' ? childIds : undefined,
      };

      positionedNodes.push(positionedNode);

      // 将子节点放置在分组内部
      if (node.type === 'dynamic-group' && children.length > 0) {
        const stateWidth = 70;
        const stateHeight = 30;
        const statesPerRow = 2;
        const stateSpacing = 10;
        const padding = 30;

        // 计算分组内部的起始位置（左上角）
        const groupLeft = x - width / 2 + padding;
        const groupTop = y - height / 2 + padding + 20; // +20 为分组标题留空间

        children.forEach((child, childIndex) => {
          const row = Math.floor(childIndex / statesPerRow);
          const col = childIndex % statesPerRow;

          const childX =
            groupLeft + col * (stateWidth + stateSpacing) + stateWidth / 2;
          const childY =
            groupTop + row * (stateHeight + stateSpacing) + stateHeight / 2;

          // 根据类型完善属性
          let childProperties: Record<string, any>;
          if (child.type === 'state') {
            childProperties = completeStateNodeProperties(child);
          } else if (child.type === 'value') {
            childProperties = completeValueNodeProperties(child);
          } else {
            childProperties = {
              ...child.properties,
              parentGroupId: node.id,
              width: stateWidth,
              height: stateHeight,
            };
          }

          const positionedChild: PositionedNode = {
            id: child.id,
            type: child.type,
            x: childX,
            y: childY,
            width: stateWidth,
            height: stateHeight,
            text: {
              value: child.properties?.name || '',
              x: childX,
              y: childY,
            },
            properties: childProperties,
          };

          positionedNodes.push(positionedChild);
        });
      }
    });
  });

  return {
    nodes: positionedNodes,
    edges: importedEdges,
  };
};

/**
 * 默认布局：当找不到目标节点时使用
 */
const generateDefaultPositions = (
  nodes: ImportedNode[],
  edges: ImportedEdge[],
  startX: number,
  startY: number,
  existingNodes: ExistingNode[]
): { nodes: PositionedNode[]; edges: ImportedEdge[] } => {
  // 分离子节点和顶级节点
  const childNodes = nodes.filter(n => isChildNode(n));
  const topLevelNodes = nodes.filter(n => !isChildNode(n));

  // 构建父子关系映射
  const parentChildMap = new Map<string, ImportedNode[]>();
  childNodes.forEach(child => {
    const parentId = child.properties?.parentGroupId;
    if (parentId) {
      if (!parentChildMap.has(parentId)) {
        parentChildMap.set(parentId, []);
      }
      parentChildMap.get(parentId)!.push(child);
    }
  });

  const positionedNodes: PositionedNode[] = [];
  const gridSpacing = 220;
  const nodesPerRow = 3;

  topLevelNodes.forEach((node, index) => {
    const row = Math.floor(index / nodesPerRow);
    const col = index % nodesPerRow;

    // 获取该分组的子节点
    const children = parentChildMap.get(node.id) || [];
    const childIds = children.map(c => c.id);

    const { width, height } = getDefaultNodeSize(node.type, children.length);

    let x = startX + col * gridSpacing;
    let y = startY + row * gridSpacing;

    // 避免与现有节点重叠
    let attempts = 0;
    while (
      !isPositionValid(x, y, width, height, existingNodes, positionedNodes) &&
      attempts < 20
    ) {
      x += gridSpacing / 2;
      attempts++;
    }

    // 构建完整的节点属性
    let properties: Record<string, any>;
    let textY = y; // 默认文字在中心

    if (node.type === 'dynamic-group') {
      properties = completeGroupNodeProperties(
        node,
        childIds,
        children,
        height
      );
      properties.width = width;
      properties.height = height;
      // 分组节点的文字定位到顶部
      textY = y - height / 2 + 20;
    } else {
      properties = {
        ...node.properties,
        width,
        height,
      };
    }

    const positionedNode: PositionedNode = {
      id: node.id,
      type: node.type,
      x,
      y,
      width,
      height,
      text: {
        value: node.properties?.name || '',
        x,
        y: textY,
      },
      properties,
      children: node.type === 'dynamic-group' ? childIds : undefined,
    };

    positionedNodes.push(positionedNode);

    // 将子节点放置在分组内部
    if (node.type === 'dynamic-group' && children.length > 0) {
      const stateWidth = 70;
      const stateHeight = 30;
      const statesPerRow = 2;
      const stateSpacing = 10;
      const padding = 30;

      const groupLeft = x - width / 2 + padding;
      const groupTop = y - height / 2 + padding + 20;

      children.forEach((child, childIndex) => {
        const childRow = Math.floor(childIndex / statesPerRow);
        const childCol = childIndex % statesPerRow;

        const childX =
          groupLeft + childCol * (stateWidth + stateSpacing) + stateWidth / 2;
        const childY =
          groupTop + childRow * (stateHeight + stateSpacing) + stateHeight / 2;

        let childProperties: Record<string, any>;
        if (child.type === 'state') {
          childProperties = completeStateNodeProperties(child);
        } else if (child.type === 'value') {
          childProperties = completeValueNodeProperties(child);
        } else {
          childProperties = {
            ...child.properties,
            parentGroupId: node.id,
            width: stateWidth,
            height: stateHeight,
          };
        }

        positionedNodes.push({
          id: child.id,
          type: child.type,
          x: childX,
          y: childY,
          width: stateWidth,
          height: stateHeight,
          text: {
            value: child.properties?.name || '',
            x: childX,
            y: childY,
          },
          properties: childProperties,
        });
      });
    }
  });

  return { nodes: positionedNodes, edges };
};

/**
 * 处理导入数据的完整流程
 */
export const processImportedData = (
  rawData: ImportedData,
  targetNodeId: string,
  existingNodes: ExistingNode[]
): { nodes: any[]; edges: any[] } => {
  const { nodes: positionedNodes, edges } = generateNodePositions(
    rawData,
    targetNodeId,
    existingNodes
  );

  // 转换为 LogicFlow 格式
  const logicFlowNodes = positionedNodes.map(node => ({
    id: node.id,
    type: node.type,
    x: node.x,
    y: node.y,
    width: node.width,
    height: node.height,
    text: node.text,
    properties: node.properties,
    children: node.children,
  }));

  const logicFlowEdges = edges.map(edge => ({
    id: edge.id,
    type: edge.type,
    sourceNodeId: edge.sourceNodeId,
    targetNodeId: edge.targetNodeId,
    text: edge.text || '',
    properties: edge.properties || {},
  }));

  return {
    nodes: logicFlowNodes,
    edges: logicFlowEdges,
  };
};
